import {
  useCallback,
  type KeyboardEvent,
  type ReactNode,
  type RefObject,
} from "react";
import {
  Command,
  CommandInput,
  CommandList,
} from "@/components/molecules/command";
import { cn } from "@/lib/utils";
import { PickerListRow } from "./pickerListRow";

export interface PickerListItem {
  raw: string;
  label: string;
}

export interface PickerListProps {
  items: PickerListItem[];
  onSelect: (item: PickerListItem) => void;
  /** For keyboard nav in floating context; omit in popover. */
  selectedIndex?: number;
  /** When selection moves (cmdk), notify parent for controlled highlight sync. */
  onHighlightChange?: (index: number) => void;
  /** Optional id prefix; values passed to cmdk are `${prefix}::${raw}` to avoid collisions. */
  optionIdPrefix?: string;
  /** Optional ref for the first list item (cmdk `CommandItem` root; e.g. focus after auto-open). */
  firstItemRef?: RefObject<HTMLDivElement | null>;
  /** Ref for the cmdk root (focus management in popovers). */
  containerRef?: RefObject<HTMLDivElement | null>;
  className?: string;
  /** Optional empty state when items.length === 0. */
  emptyMessage?: ReactNode;
  /** Popover: close on Escape (in addition to Radix). */
  onEscape?: () => void;
  /** Optional filter row above the list (parent-driven filtering; Command has shouldFilter=false). */
  filterValue?: string;
  onFilterChange?: (value: string) => void;
  filterInputRef?: RefObject<HTMLInputElement | null>;
  filterPlaceholder?: string;
  /** Visually hidden label for the filter field (accessibility). */
  filterAriaLabel?: string;
}

const itemValue = (optionIdPrefix: string, raw: string) =>
  `${optionIdPrefix}::${raw}`;

export const PickerList = ({
  items,
  onSelect,
  selectedIndex = 0,
  onHighlightChange,
  optionIdPrefix = "picker",
  firstItemRef,
  containerRef,
  className,
  emptyMessage,
  onEscape,
  filterValue,
  onFilterChange,
  filterInputRef,
  filterPlaceholder,
  filterAriaLabel,
}: PickerListProps): ReactNode => {
  const safeIndex = Math.min(
    Math.max(0, selectedIndex),
    Math.max(0, items.length - 1),
  );
  const value =
    items.length > 0
      ? itemValue(optionIdPrefix, items[safeIndex]?.raw ?? "")
      : "";

  /** When the filtered set changes but length stays the same, scroll must still run. */
  const itemsScrollKey = items.map((it) => it.raw).join("\0");

  const handleValueChange = useCallback(
    (next: string) => {
      const prefix = `${optionIdPrefix}::`;
      if (!next.startsWith(prefix)) return;
      const raw = next.slice(prefix.length);
      const index = items.findIndex((it) => it.raw === raw);
      if (index >= 0) onHighlightChange?.(index);
    },
    [items, onHighlightChange, optionIdPrefix],
  );

  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      if (containerRef != null) {
        containerRef.current = el;
      }
    },
    [containerRef],
  );

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Escape" && onEscape != null) {
      e.preventDefault();
      onEscape();
    }
  };

  const showFilter = onFilterChange != null;
  const hasListItems = items.length > 0;

  if (!hasListItems && emptyMessage != null && !showFilter) {
    return (
      <div className={cn("px-3 py-2 text-sm", className)}>{emptyMessage}</div>
    );
  }
  if (!hasListItems && !showFilter) return null;

  return (
    <Command
      ref={setContainerRef}
      shouldFilter={false}
      value={value}
      onValueChange={handleValueChange}
      loop
      className={cn(
        `
          rounded-md outline-none
          focus-visible:ring-1 focus-visible:ring-ring
        `,
        className,
      )}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {showFilter ? (
        <CommandInput
          ref={filterInputRef}
          value={filterValue ?? ""}
          onValueChange={(v) => {
            onFilterChange(v);
          }}
          placeholder={filterPlaceholder}
          aria-label={filterAriaLabel}
        />
      ) : null}
      {!hasListItems && emptyMessage != null ? (
        <div className="px-3 py-2 text-sm">{emptyMessage}</div>
      ) : null}
      {hasListItems ? (
        <CommandList>
          {items.map((item, i) => {
            const v = itemValue(optionIdPrefix, item.raw);
            return (
              <PickerListRow
                key={v}
                item={item}
                value={v}
                isHighlighted={i === safeIndex}
                itemsScrollKey={itemsScrollKey}
                onSelect={() => {
                  onSelect(item);
                }}
                firstItemRef={firstItemRef}
                isFirst={i === 0}
              />
            );
          })}
        </CommandList>
      ) : null}
    </Command>
  );
};
